import httpClient from './httpClient';
import { LONG_ORCHESTRATOR_REQUEST_MS } from './apiTimeouts';
import { API_CONFIG } from '../utils/constants';

// Types for User Profile
export interface OnboardingStatus {
  status: 'pending_preferences' | 'pending_resume' | 'pending_first_session' | 'complete';
  step: number;
  complete: boolean;
  steps: {
    '0_preferences': boolean;
    '1_resume_upload': boolean;
    '2_first_session': boolean;
  };
}

export interface ResumeSummary {
  original_filename: string;
  uploaded_at: string;
  num_pages: number;
}

export interface FairUsageAgreement {
  agreed: boolean;
  agreed_at: string;
  version: string;
  needs_update: boolean;
  current_version: string;
}

export interface UserProfile {
  user_id: string;
  user_email: string;
  first_name?: string;
  last_name?: string;
  fair_usage_agreement?: FairUsageAgreement;
  onboarding: OnboardingStatus;
  has_preferences: boolean;
  has_resume: boolean;
  resume_summary: ResumeSummary | null;
  sessions: any[];
  token_allocated: number | null;
  token_used: number | null;
  created_at: string;
  updated_at: string;
  /** Legacy GET user/profile fields (org-linked students). Prefer org_name / dept_name when present. */
  organization_name?: string;
  department_name?: string;
  /** GET user/profile — primary org/dept labels for org-linked students. */
  org_name?: string;
  dept_name?: string;
}

/** Normalized org/department for UI; omits empty strings after trim. */
export function getOrgDepartmentLabels(data: UserProfile): {
  organizationName?: string;
  departmentName?: string;
} {
  const organizationName =
    data.org_name?.trim() || data.organization_name?.trim() || undefined;
  const departmentName =
    data.dept_name?.trim() || data.department_name?.trim() || undefined;
  return { organizationName, departmentName };
}

export interface UserProfileResponse {
  success: boolean;
  data: UserProfile;
}

// Types for Resume Upload
export interface ContactDetails {
  name: string;
  email: string;
  phone: string;
  linkedin: string;
  raw?: string;
}

export interface ResumeUploadData {
  s3_url: string;
  original_filename: string;
  num_pages: number;
  contact_details: ContactDetails;
  skills_count: number;
  experience_count: number;
  education_count: number;
}

export interface ResumeUploadResponse {
  success: boolean;
  message: string;
  data: ResumeUploadData;
}

// Types for Get Resume
export interface ExperienceEntry {
  company: string;
  location: string;
  role: string;
  start_date: string;
  end_date: string;
  description: string;
}

export interface EducationEntry {
  institution: string;
  location: string;
  degree: string;
  start_date: string;
  end_date: string;
  description: string;
}

export interface ParsedResumeData {
  contact_details: ContactDetails;
  experience: {
    entries: ExperienceEntry[];
    raw_text: string;
  };
  education: {
    entries: EducationEntry[];
    raw_text: string;
  };
  skills_and_achievements: {
    skills_section: string;
    achievements_section: string;
    extracted_skills: string[];
  };
  additional_info: {
    projects: string;
    summary: string;
    other: string;
  };
}

export interface ResumeData {
  s3_key: string;
  s3_url: string;
  original_filename: string;
  num_pages: number;
  parsed_data: ParsedResumeData;
  uploaded_at: string;
}

export interface GetResumeResponse {
  success: boolean;
  data: ResumeData;
}

export interface DeleteResumeResponse {
  success: boolean;
  message: string;
}

// Types for Onboarding Interview
export interface OnboardingInterviewStartResponse {
  success: boolean;
  question: string;
  audio_url?: string;
  audio_mode?: string;
  message?: string;
  session_id?: string;
  max_questions?: number;
}

// API functions
export const onboardingApi = {
  /**
   * Get user profile with onboarding status
   * Uses BASE_URL from constants: https://api.letuscrackit.ai/api/
   */
  async getUserProfile(): Promise<UserProfileResponse> {
    const response = await httpClient.get('user/profile');
    return response.data;
  },

  /**
   * Upload resume PDF file
   * Uses BASE_URL from constants: https://api.letuscrackit.ai/api/
   * @param file - PDF file to upload
   * @param onUploadProgress - Optional callback for upload progress
   */
  async uploadResume(
    file: File,
    onUploadProgress?: (progressEvent: { loaded: number; total?: number }) => void
  ): Promise<ResumeUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);

    const token = localStorage.getItem('authToken');

    try {
      // Use native fetch API for FormData upload - this properly handles multipart/form-data
      // NOTE: Do NOT set Content-Type header - browser sets it with correct boundary for FormData
      const fetchResponse = await fetch(`${API_CONFIG.BASE_URL}orchestrator/resume/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          // Intentionally NOT setting Content-Type - browser auto-sets for FormData
        },
        body: formData,
      });

      if (!fetchResponse.ok) {
        const errorData = await fetchResponse.json().catch(() => ({}));
        const error: any = new Error(`Request failed with status ${fetchResponse.status}`);
        error.response = { status: fetchResponse.status, data: errorData };
        throw error;
      }

      const data = await fetchResponse.json();
      return data;
    } catch (error: any) {
      throw error;
    }
  },

  /**
   * Get parsed resume data
   * Uses BASE_URL from constants: https://api.letuscrackit.ai/api/
   */
  async getResume(): Promise<GetResumeResponse> {
    const response = await httpClient.get('onboarding/resume');
    return response.data;
  },

  /**
   * Delete user's resume
   * Uses BASE_URL from constants: https://api.letuscrackit.ai/api/
   */
  async deleteResume(): Promise<DeleteResumeResponse> {
    const response = await httpClient.delete('onboarding/resume');
    return response.data;
  },

  /**
   * Save user preferences (goal and aspiration) during onboarding
   * Uses BASE_URL from constants: https://api.letuscrackit.ai/api/
   */
  async savePreferences(preferences: { goal: string; aspiration: string }): Promise<any> {
    const response = await httpClient.post('user/preferences', preferences);
    return response.data;
  },

  /**
   * Start onboarding interview for new users (context-first via orchestrator).
   * Uses BASE_URL from constants: https://api.letuscrackit.ai/api/
   */
  async startOnboardingInterview(): Promise<OnboardingInterviewStartResponse> {
    const response = await httpClient.post(
      'orchestrator/onboarding/start-interview',
      {},
      { timeout: LONG_ORCHESTRATOR_REQUEST_MS }
    );
    return response.data;
  },
};

// Error handling helper
export const handleOnboardingError = (error: any): string => {
  if (error.response?.data?.detail) {
    const detail = error.response.data.detail;
    // Handle Pydantic validation errors (array of {type, loc, msg, input, url})
    if (Array.isArray(detail)) {
      return detail.map((d: any) => d.msg || String(d)).join(', ');
    }
    // Handle string detail
    if (typeof detail === 'string') {
      return detail;
    }
    // Handle object detail
    if (typeof detail === 'object' && detail.msg) {
      return detail.msg;
    }
    return String(detail);
  }

  if (error.response?.data?.message) {
    return error.response.data.message;
  }

  if (error.response?.status === 400) {
    const detail = error.response.data?.detail;
    if (Array.isArray(detail)) {
      return detail.map((d: any) => d.msg || String(d)).join(', ');
    }
    return detail || 'Invalid file. Please upload a PDF file.';
  }

  if (error.response?.status === 404) {
    return 'No resume found. Please upload your resume.';
  }

  if (error.response?.status === 401) {
    return 'Authentication failed. Please log in again.';
  }

  if (error.response?.status === 500) {
    return 'Server error. Please try again later.';
  }

  if (error.code === 'NETWORK_ERROR' || !error.response) {
    return 'Network error. Please check your connection.';
  }

  return 'An unexpected error occurred. Please try again.';
};

export default onboardingApi;

